import { registerArtifactPayload } from '@/features/documentPreview/artifactPayloadStore'
import {
  ZOHO_CRM_INTEGRATION_DEMO_ARTIFACT_ID,
  ZOHO_STREAM_THINKING_PAUSE_MS,
} from '../demo/zohoCrmIntegrationDemo.js'
import {
  ZOHO_CRM_INTEGRATION_AI_LOG_STEPS_BUILD,
  ZOHO_CRM_INTEGRATION_AI_LOG_STEPS_INITIAL,
  ZOHO_CRM_INTEGRATION_BEFORE_BUILD_MARKDOWN,
  ZOHO_CRM_INTEGRATION_INTRO_MARKDOWN,
  ZOHO_CRM_INTEGRATION_THOUGHT_DETAILS,
  buildZohoCrmIntegrationArtifactPayload,
  buildZohoCrmIntegrationFinalBlocks,
} from '../demo/zohoCrmIntegrationDemoContent.js'
import { NEXUS_CRM_INTEGRATION_ARTIFACT_FILE_NAME } from '../demo/nexusIntegrationsDemo.js'
import {
  computeStreamTypingDuration,
  randomPickPauseMs,
  scaleStreamDelay,
} from '../streaming/computeStreamTypingDuration.js'
import { CHAT_STREAM_CONTENT_SPEED_FACTOR, randomPreamblePauseMs } from '../streaming/chatStreamSpeed.js'
import { runTypewriter } from '../streaming/runTypewriter.js'
import { createDemoSelectionController } from '../demo/demoSelectionSwitch.js'
import { updatePendingZohoDemoMessage } from './patchZohoCrmIntegrationDemoMessage.js'
import { runWorkflowStepsInStream } from './runWorkflowStepsInStream.js'

export function startZohoCrmIntegrationDemoStreamLoop(ctx) {
  const { tid, pendingId, signal, session, setMessagesByThreadId } = ctx

  const state = {
    thoughtDetails: /** @type {string[]} */ ([]),
    blocks: /** @type {object[]} */ ([]),
    artifactId: ZOHO_CRM_INTEGRATION_DEMO_ARTIFACT_ID,
  }

  const patch = (patchFields) => {
    updatePendingZohoDemoMessage(setMessagesByThreadId, tid, pendingId, patchFields)
  }

  const selection = createDemoSelectionController(state, patch)

  const delay = (ms) => scaleStreamDelay(ms, CHAT_STREAM_CONTENT_SPEED_FACTOR)
  const randomThinkingPause = () => randomPreamblePauseMs()
  const zohoTypewriterDuration = (text) =>
    computeStreamTypingDuration(text.length, {
      min: randomPickPauseMs(ZOHO_STREAM_THINKING_PAUSE_MS),
      max: 6000,
    })

  const schedule = (ms, fn) => {
    session.addTimeout(() => {
      if (signal.aborted) {
        session.dispose()
        return
      }
      fn()
    }, ms)
  }

  const runAfter = (ms, fn) => schedule(delay(ms), fn)

  const runTypewriterText = (text, onUpdate, onDone) => {
    runTypewriter({
      session,
      signal,
      text,
      durationMs: delay(zohoTypewriterDuration(text)),
      onUpdate,
      onDone,
    })
  }

  const runPreambleThinking = (onDone) => {
    patch({
      demoStreamActive: true,
      thoughtSummary: 'Thinking…',
      thoughtDetails: [],
      blocks: [],
    })
    selection.setInitial({
      agentId: 'software-engineer',
      personaId: 'swe',
    })
    schedule(randomThinkingPause(), onDone)
  }

  const runThoughtParagraphs = (onDone) => {
    let paraIdx = 0

    const runNextParagraph = () => {
      if (paraIdx >= ZOHO_CRM_INTEGRATION_THOUGHT_DETAILS.length) {
        patch({ thoughtSummary: 'Worked for 3m 19s', thoughtDetails: [...state.thoughtDetails] })
        runAfter(400, onDone)
        return
      }

      const fullText = ZOHO_CRM_INTEGRATION_THOUGHT_DETAILS[paraIdx]
      state.thoughtDetails.push('')
      const currentParaIndex = state.thoughtDetails.length - 1
      patch({ thoughtDetails: [...state.thoughtDetails] })

      runTypewriterText(
        fullText,
        (partial) => {
          state.thoughtDetails[currentParaIndex] = partial
          patch({ thoughtDetails: [...state.thoughtDetails] })
        },
        () => {
          paraIdx++
          runAfter(300, runNextParagraph)
        },
      )
    }

    runNextParagraph()
  }

  const runMarkdownType = (content, onDone) => {
    const blockIndex = state.blocks.length
    state.blocks.push({ type: 'markdown', content: '' })
    patch({ blocks: [...state.blocks] })
    runTypewriterText(
      content,
      (partial) => {
        state.blocks[blockIndex] = { type: 'markdown', content: partial }
        patch({ blocks: [...state.blocks] })
      },
      onDone,
    )
  }

  const runHeadingType = (level, content, onDone) => {
    const blockIndex = state.blocks.length
    state.blocks.push({ type: 'heading', level, content: '' })
    patch({ blocks: [...state.blocks] })
    runTypewriterText(
      content,
      (partial) => {
        state.blocks[blockIndex] = { type: 'heading', level, content: partial }
        patch({ blocks: [...state.blocks] })
      },
      onDone,
    )
  }

  /**
   * @param {{ type: 'list', items: string[], ordered?: boolean }} block
   * @param {() => void} onDone
   */
  const runListType = (block, onDone) => {
    const blockIndex = state.blocks.length
    const items = block.items ?? []
    state.blocks.push({ type: 'list', items: [], ordered: block.ordered })
    patch({ blocks: [...state.blocks] })

    let itemIdx = 0

    const runNextItem = () => {
      if (itemIdx >= items.length) {
        onDone()
        return
      }

      const itemText = items[itemIdx]
      state.blocks[blockIndex].items.push('')
      const currentItemIndex = itemIdx
      patch({ blocks: [...state.blocks] })

      runTypewriterText(
        itemText,
        (partial) => {
          state.blocks[blockIndex].items[currentItemIndex] = partial
          patch({ blocks: [...state.blocks] })
        },
        () => {
          itemIdx++
          runAfter(200, runNextItem)
        },
      )
    }

    runNextItem()
  }

  /**
   * @param {{ type: 'table', data: unknown[] }} block
   * @param {() => void} onDone
   */
  const runTableReveal = (block, onDone) => {
    const blockIndex = state.blocks.length
    const rows = block.data ?? []
    state.blocks.push({ type: 'table', data: [] })
    patch({ blocks: [...state.blocks] })

    let rowIdx = 0

    const addNextRow = () => {
      if (rowIdx >= rows.length) {
        onDone()
        return
      }
      state.blocks[blockIndex].data.push(rows[rowIdx])
      patch({ blocks: [...state.blocks] })
      rowIdx++
      runAfter(280, addNextRow)
    }

    addNextRow()
  }

  /**
   * @param {object[]} templates
   * @param {() => void} onDone
   */
  const runWorkflowSteps = (templates, onDone) => {
    const logIndex = state.blocks.length
    state.blocks.push({ type: 'aiResponseLog', status: 'ok', showLogStatus: false, steps: [] })

    const patchLog = (steps) => {
      state.blocks[logIndex] = {
        type: 'aiResponseLog',
        status: 'ok',
        showLogStatus: false,
        steps,
      }
      patch({ blocks: [...state.blocks] })
    }

    runWorkflowStepsInStream({
      templates,
      session,
      signal,
      runAfter: schedule,
      onStepsChange: patchLog,
      onDone,
      speedFactor: CHAT_STREAM_CONTENT_SPEED_FACTOR,
      getThinkingPauseMs: randomThinkingPause,
      getTypewriterDurationMs: zohoTypewriterDuration,
    })
  }

  const runInstantFileArtifact = (onDone) => {
    registerArtifactPayload({
      artifactId: state.artifactId,
      kind: 'doc',
      fileName: NEXUS_CRM_INTEGRATION_ARTIFACT_FILE_NAME,
      description: 'Markdown',
      payload: buildZohoCrmIntegrationArtifactPayload(),
    })
    state.blocks.push({
      type: 'fileArtifact',
      fileName: NEXUS_CRM_INTEGRATION_ARTIFACT_FILE_NAME,
      description: 'Markdown',
      artifactKind: 'doc',
      artifactId: state.artifactId,
    })
    patch({ blocks: [...state.blocks] })
    runAfter(300, onDone)
  }

  const runTailBlocks = () => {
    const tail = buildZohoCrmIntegrationFinalBlocks(state.artifactId).slice(5)

    let i = 0

    const addNext = () => {
      if (i >= tail.length) {
        patch({
          demoStreamActive: false,
          thoughtSummary: 'Worked for 3m 19s',
          blocks: [...state.blocks],
        })
        session.dispose()
        return
      }

      const block = tail[i]

      if (block.type === 'markdown' && typeof block.content === 'string') {
        const blockIndex = state.blocks.length
        state.blocks.push({ type: 'markdown', content: '', variant: block.variant })
        patch({ blocks: [...state.blocks] })
        runTypewriterText(
          block.content,
          (partial) => {
            state.blocks[blockIndex] = { type: 'markdown', content: partial, variant: block.variant }
            patch({ blocks: [...state.blocks] })
          },
          () => {
            i++
            runAfter(200, addNext)
          },
        )
        return
      }

      if (block.type === 'heading' && typeof block.content === 'string') {
        runHeadingType(block.level, block.content, () => {
          i++
          runAfter(200, addNext)
        })
        return
      }

      if (block.type === 'list' && Array.isArray(block.items)) {
        runListType(block, () => {
          i++
          runAfter(200, addNext)
        })
        return
      }

      if (block.type === 'table' && Array.isArray(block.data)) {
        runTableReveal(block, () => {
          i++
          runAfter(200, addNext)
        })
        return
      }

      state.blocks.push(block)
      patch({ blocks: [...state.blocks] })
      i++
      runAfter(200, addNext)
    }

    addNext()
  }

  runPreambleThinking(() => {
    runThoughtParagraphs(() => {
      runMarkdownType(ZOHO_CRM_INTEGRATION_INTRO_MARKDOWN, () => {
        runWorkflowSteps(ZOHO_CRM_INTEGRATION_AI_LOG_STEPS_INITIAL, () => {
          runMarkdownType(ZOHO_CRM_INTEGRATION_BEFORE_BUILD_MARKDOWN, () => {
            runWorkflowSteps(ZOHO_CRM_INTEGRATION_AI_LOG_STEPS_BUILD, () => {
              runInstantFileArtifact(() => {
                runTailBlocks()
              })
            })
          })
        })
      })
    })
  })
}
